import Head from "next/head";
import type { ReactElement } from "react";
import Image from "next/image";
import { PageLayout } from "@/components/Layout";
import { usePathname } from "next/navigation";
import { env } from "@/config/env";

import ProductsHeroSection from "@/components/ProductHeroSection";

import ProductKeyFeaturesSection from "@/components/ProductKeyFeaturesSection";
import ProductSolutionsSection from "@/components/ProductSolutionsSection";
import ProductAPIIntegrationSection from "@/components/ProductAPIIntegrationSection";
import ProductWhyChooseUs from "@/components/ProductWhyChooseUsSection";
import ProductCallToActionSection from "@/components/ProductCallToActionSection";
import ProductInsightsAndInspirationsSection from "@/components/ProductInsightsAndInspirationsSection";
import { TOKENGATING_ARTICLES } from "@/containers/Insights/constants";
import {
  PRODUCT_TOKEN_GATING_BENEFITS,
  PRODUCT_TOKEN_GATING_CTA,
  PRODUCT_TOKEN_GATING_INFORMATION,
  PRODUCT_TOKEN_GATING_INTEGRATION,
  PRODUCT_TOKEN_GATING_KEY_FEATURES,
  PRODUCT_TOKEN_GATING_SOLUTIONS,
} from "@/constants/products/token-gating";

const TokengatingPage = () => {
  const pathname = usePathname();
  const url = `https://${env.APP_DOMAIN}${pathname}`;
  const path = pathname.split("/")[2];

  return (
    <>
      <Head>
        <title>Mintology | {PRODUCT_TOKEN_GATING_INFORMATION.title} </title>
        <meta
          name="description"
          content={PRODUCT_TOKEN_GATING_INFORMATION.description}
        />
        <meta
          property="og:title"
          content={`${PRODUCT_TOKEN_GATING_INFORMATION.title} | Mintology`}
        />
        <meta
          property="og:description"
          content={`${PRODUCT_TOKEN_GATING_INFORMATION.description} | `}
        />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Mintology" />
        <meta
          property="og:image"
          content={PRODUCT_TOKEN_GATING_INFORMATION.image}
        />
        <meta
          property="og:image:alt"
          content={PRODUCT_TOKEN_GATING_INFORMATION.imageInfo}
        />
        <meta property="og:url" content={url} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={url} />
      </Head>
      <main className="pt-[4rem] px-7 lg:pt-[5rem] pb-12 container mx-auto flex flex-col gap-10 md:gap-28 lg:px-6 relative">
        <Image
          priority
          src={"/images/products/products-hero-background.png"}
          alt="Products Hero Background Image"
          height={300}
          width={500}
          className="object-contain absolute top-0 -z-10"
        />
        <ProductsHeroSection
          title={PRODUCT_TOKEN_GATING_INFORMATION.title}
          description={PRODUCT_TOKEN_GATING_INFORMATION.description}
          image={PRODUCT_TOKEN_GATING_INFORMATION.image}
          highlight={PRODUCT_TOKEN_GATING_INFORMATION.highlight}
          bgImage={PRODUCT_TOKEN_GATING_INFORMATION.bgImage}
          buttonText={PRODUCT_TOKEN_GATING_INFORMATION.buttonText}
        />

        <ProductSolutionsSection
          title={PRODUCT_TOKEN_GATING_SOLUTIONS.title}
          highlight={PRODUCT_TOKEN_GATING_SOLUTIONS.highlight}
          solutions={PRODUCT_TOKEN_GATING_SOLUTIONS.solutions}
        />

        <section className="">
          <div className="flex flex-col justify-center mx-auto items-center gap-2 w-full ">
            <span className="md:text-[45px] text-2xl font-bold text-center leading-tight">
              <span className="bg-gradient-to-r from-[#4337cc] to-primary-900 bg-clip-text text-transparent">
                Solving Token Gating’s
              </span>{" "}
              Biggest Headaches
            </span>
            <span className="text-center text-base md:text-xl md:w-4/5">
              Most token gating solutions out there are clunky, expensive, and
              hard to integrate. This leads to broken user experiences and
              frustrated customers.
            </span>
          </div>
          <div className="bg-gradient-to-br from-purple-200 mix-blend-overlay via-[#fcfcfc]  to-[#f3d6d6] flex flex-col w-full gap-10  mt-10 p-12 rounded-2xl">
            <span className="md:text-[32px] text-2xl  font-bold text-center">
              Other Token Gating systems?
              <span className="bg-gradient-to-r from-[#4337cc] to-primary-900 bg-clip-text text-transparent">
                {" "}
                Clunky, confusing, and costly.
              </span>{" "}
            </span>
            <div className="flex flex-col md:flex-row gap-4 xl:gap-0">
              <div className="rounded-lg md:w-1/2 ">
                <Image
                  priority
                  src={"/images/products/token-gate-confusion.png"}
                  alt="Onboarding image"
                  height={200}
                  width={500}
                  className="object-contain rounded-md"
                />
              </div>
              <div className="md:w-1/2 flex flex-col gap-4 justify-between">
                <div className="flex flex-col gap-4">
                  <span className="border-gray-200 border-2 rounded-full px-4 py-1 bg-gradient-to-r from-[#4337cc] to-primary-900 bg-clip-text text-transparent font-semibold w-28">
                    Problem!
                  </span>
                  <span className="text-secondary-900 text-xl md:text-2xl font-bold">
                    How Mintology Solves It:
                  </span>
                  <span className="text-zinc-500 text-base md:text-lg">
                    Stop losing users to confusing access flows. Mintology’s
                    APIs handle spikes in demand, enforce rules securely, and
                    integrate like a native feature so your dev team builds what
                    matters, not gatekeeping logic. Launch in days. Scale
                    forever.
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <ProductKeyFeaturesSection
          keyFeatures={PRODUCT_TOKEN_GATING_KEY_FEATURES.features}
          title={PRODUCT_TOKEN_GATING_KEY_FEATURES.title}
          highlight={PRODUCT_TOKEN_GATING_KEY_FEATURES.highlight}
          description={PRODUCT_TOKEN_GATING_KEY_FEATURES.description}
        />

        <ProductAPIIntegrationSection
          title={PRODUCT_TOKEN_GATING_INTEGRATION.title}
          highlight={PRODUCT_TOKEN_GATING_INTEGRATION.highlight}
          description={PRODUCT_TOKEN_GATING_INTEGRATION.description}
          path={path}
        />

        <ProductWhyChooseUs
          title={PRODUCT_TOKEN_GATING_BENEFITS.title}
          highlight={PRODUCT_TOKEN_GATING_BENEFITS.highlight}
          benefits={PRODUCT_TOKEN_GATING_BENEFITS.benefits}
        />

        <ProductCallToActionSection
          title={PRODUCT_TOKEN_GATING_CTA.title}
          highlight={PRODUCT_TOKEN_GATING_CTA.highlight}
          buttonText={PRODUCT_TOKEN_GATING_CTA.buttonText}
        />

        <ProductInsightsAndInspirationsSection
          title="Insights & Inspiration"
          highlight="Insights"
          articles={TOKENGATING_ARTICLES}
          titleHeight={105}
        />
      </main>
    </>
  );
};

TokengatingPage.getLayout = (page: ReactElement) => {
  return <PageLayout>{page}</PageLayout>;
};

export default TokengatingPage;
